import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { C, ACCENT } from '../../theme';
import { digitalApi as api } from './api';
import { useToast } from '../../components/Toast';

const AGENCY_ROLES = ['admin','account_manager','senior_planner','junior_planner','client_view','brand_view'];
const ADVERTISER_ROLES = ['admin','senior_planner','junior_planner','client_view','brand_view'];

const ROLE_LABEL = {
  admin:'Admin',
  account_manager:'Account Manager',
  senior_planner:'Senior Planner',
  junior_planner:'Junior Planner',
  client_view:'Client View',
  brand_view:'Brand View',
};
const ADVERTISER_ROLE_LABEL = {
  admin:'Admin',
  senior_planner:'User (account)',
  junior_planner:'User (brand)',
  client_view:'View (account)',
  brand_view:'View (brand)',
};
const ROLE_VARIANT = { admin:'danger', account_manager:'info', senior_planner:'success', junior_planner:'neutral', client_view:'neutral', brand_view:'neutral', super_admin:'danger' };

// Roles that require a client assignment (agency only)
const ROLES_NEED_CLIENT = new Set(['account_manager','client_view']);
// Roles that require a brand assignment
const ROLES_NEED_BRAND = new Set(['junior_planner','brand_view']);

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(80,120,160,0.25)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }} onClick={onClose}>
      <div style={C.modal} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:'#1a2a3a' }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#8aa0b8', fontSize:22, cursor:'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function authFetch(url, opts={}) {
  const token = localStorage.getItem('plan_token');
  return fetch(url, { ...opts, headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}`, ...(opts.headers||{}) } });
}

export default function UsersPage() {
  const { user: me, isAdmin, isAgency } = useAuth();
  const { showToast } = useToast();
  const ROLES = isAgency ? AGENCY_ROLES : ADVERTISER_ROLES;
  const RLABEL = isAgency ? ROLE_LABEL : ADVERTISER_ROLE_LABEL;
  const BLANK = { email:'', role: isAgency ? 'junior_planner' : 'senior_planner', client_id:'', brand_id:'' };

  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resettingId, setResettingId] = useState(null);

  async function load() {
    try {
      const res = await authFetch('/api/users/');
      if (res.ok) setUsers(await res.json());
    } catch(e) {}
    setLoading(false);
  }

  useEffect(() => {
    load();
    if (isAgency) api.getClients().then(setClients).catch(()=>{});
    api.getBrands().then(setBrands).catch(()=>{});
  }, [isAgency]);

  // For agency brand-level roles: filter brands by the selected client
  const filteredBrands = useMemo(() => {
    if (isAgency && form.client_id) return brands.filter(b => String(b.client_id) === String(form.client_id));
    if (isAgency) return [];
    return brands;
  }, [isAgency, brands, form.client_id]);

  const needsClient = isAgency && ROLES_NEED_CLIENT.has(form.role);
  const needsBrand = ROLES_NEED_BRAND.has(form.role);
  // For agency brand roles, user picks a client first to filter the brand list
  const needsClientForBrand = isAgency && needsBrand;

  function handleRoleChange(role) {
    setForm(p => ({ ...p, role, client_id: '', brand_id: '' }));
  }

  async function handleSave() {
    setError(''); setSuccess('');
    if (modal === 'new') {
      if (!form.email) { setError('Email is required'); return; }
      if (needsClient && !form.client_id) { setError('Please select a client for this role'); return; }
      if (needsBrand && !form.brand_id) { setError('Please select a brand for this role'); return; }
      setSaving(true);
      try {
        const body = { email: form.email, role: form.role };
        if (needsClient) body.client_id = parseInt(form.client_id);
        if (needsBrand) body.brand_id = parseInt(form.brand_id);
        const res = await authFetch('/api/invitations/', { method:'POST', body:JSON.stringify(body) });
        const d = await res.json();
        if (!res.ok) throw new Error(d.detail || 'Error');
        setModal(null);
        setSuccess(`Invitation sent to ${form.email}`);
        setTimeout(() => setSuccess(''), 5000);
      } catch(e){ setError(e.message); } finally{ setSaving(false); }
    } else {
      if (!form.full_name) { setError('Name is required'); return; }
      setSaving(true);
      try {
        const res = await authFetch(`/api/users/${modal.id}`, { method:'PATCH', body:JSON.stringify({ full_name: form.full_name, role: form.role }) });
        if (!res.ok) { const d=await res.json(); throw new Error(d.detail||'Error'); }
        await load(); setModal(null);
      } catch(e){ setError(e.message); } finally{ setSaving(false); }
    }
  }

  async function handleResetAccess(u) {
    setResettingId(u.id);
    try {
      const res = await authFetch(`/api/users/${u.id}/reset-access`, { method:'POST' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Error');
      showToast(`Password reset email sent to ${u.email}`, 'success');
    } catch(e) {
      showToast(e.message || 'Failed to send reset email', 'danger');
    } finally {
      setResettingId(null);
    }
  }

  return (
    <div style={C.page}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:28 }}>
        <h1 style={C.title}>Users <span style={{ color:'#8aa0b8', fontWeight:400, fontSize:16 }}>({users.length})</span></h1>
        {isAdmin && <button style={C.btnPrimary} onClick={()=>{setForm({...BLANK});setError('');setSuccess('');setModal('new');}}>+ Invite User</button>}
      </div>

      {loading ? <div style={C.empty}>Loading…</div> : (
        <table style={C.table}>
          <thead>
            <tr>
              <th style={C.th}>Name</th>
              <th style={C.th}>Email</th>
              <th style={C.th}>Role</th>
              <th style={C.th}>Added</th>
              {isAdmin && <th style={C.th}></th>}
            </tr>
          </thead>
          <tbody>
            {users.length===0 ? (
              <tr><td colSpan={5} style={{...C.td,textAlign:'center',color:'#8aa0b8',padding:48}}>No users yet.</td></tr>
            ) : users.map(u=>(
              <tr key={u.id} style={{...C.tr, cursor:'default'}}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(0,144,208,0.04)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <td style={{...C.td,fontWeight:600}}>{u.full_name} {u.id===me?.id&&<span style={{fontSize:10,color:ACCENT}}>(you)</span>}</td>
                <td style={{...C.td,color:'#6a8aaa'}}>{u.email}</td>
                <td style={C.td}>
                  <span style={C.statusRow()}>
                    <span style={C.statusDot(ROLE_VARIANT[u.role]||'neutral')}/>
                    {RLABEL[u.role] || ROLE_LABEL[u.role] || u.role}
                  </span>
                </td>
                <td style={{...C.td,color:'#6a8aaa'}}>{u.created_at?new Date(u.created_at).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}):'—'}</td>
                {isAdmin && (
                  <td style={{...C.td,textAlign:'right'}}>
                    <button style={{...C.btnSecondary,marginRight:8}} disabled={resettingId===u.id} onClick={()=>handleResetAccess(u)}>{resettingId===u.id?'Sending…':'Reset Access'}</button>
                    <button style={C.btnSecondary} onClick={()=>{setForm({email:u.email,full_name:u.full_name,role:u.role,client_id:'',brand_id:''});setError('');setModal(u);}}>Edit</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {success && <div style={C.feedback('success')}>{success}</div>}

      {modal && (
        <Modal title={modal==='new'?'Invite User':`Edit: ${modal.full_name}`} onClose={()=>setModal(null)}>
          {error && <div style={C.feedback('danger')}>{error}</div>}
          {modal==='new' ? (
            <>
              <div style={{fontSize:12,color:'#6a8aaa',marginBottom:16,lineHeight:1.5}}>
                An invitation link will be emailed to the address below. They'll set their own password when they accept.
              </div>
              <label style={{display:'block',marginBottom:14}}>
                <span style={C.label}>Email *</span>
                <input value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="colleague@company.com" style={C.input} autoFocus/>
              </label>
            </>
          ) : (
            <label style={{display:'block',marginBottom:14}}>
              <span style={C.label}>Full Name *</span>
              <input value={form.full_name} onChange={e=>setForm(p=>({...p,full_name:e.target.value}))} placeholder="Full name" style={C.input}/>
            </label>
          )}

          <label style={{display:'block',marginBottom: (needsClient||needsClientForBrand||needsBrand) ? 14 : 22}}>
            <span style={C.label}>Role</span>
            <select value={form.role} onChange={e=>handleRoleChange(e.target.value)} style={{...C.input}}>
              {ROLES.map(r=><option key={r} value={r}>{RLABEL[r]}</option>)}
            </select>
          </label>

          {/* Client picker — for client-level roles (agency), or to filter brand list (agency brand-level) */}
          {modal==='new' && (needsClient || needsClientForBrand) && (
            <label style={{display:'block',marginBottom:14}}>
              <span style={C.label}>{needsClient ? 'Assign to Client *' : 'Client (to filter brands) *'}</span>
              <select value={form.client_id} onChange={e=>setForm(p=>({...p,client_id:e.target.value,brand_id:''}))} style={{...C.input}}>
                <option value="">— select a client —</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          )}

          {/* Brand picker — for brand-level roles */}
          {modal==='new' && needsBrand && (
            <label style={{display:'block',marginBottom:22}}>
              <span style={C.label}>Assign to Brand *</span>
              <select value={form.brand_id} onChange={e=>setForm(p=>({...p,brand_id:e.target.value}))} style={{...C.input}}
                disabled={isAgency && !form.client_id}>
                <option value="">
                  {isAgency && !form.client_id ? '— select a client first —' : '— select a brand —'}
                </option>
                {filteredBrands.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
          )}

          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <button style={C.btnSecondary} onClick={()=>setModal(null)}>Cancel</button>
            <button style={C.btnPrimary} onClick={handleSave} disabled={saving}>{saving?(modal==='new'?'Sending…':'Saving…'):(modal==='new'?'Send Invitation':'Save')}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
